import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { getSupabaseClient } from '../lib/supabase';

export default function PayoutsRefresh() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRetry = async () => {
    try {
      setLoading(true);
      setError(null);

      const supabase = getSupabaseClient();
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        throw new Error('You must be logged in to set up payments');
      }

      const env = (window as any).__ENV__;
      const baseUrl = env?.SUPABASE_URL;

      if (!baseUrl) {
        throw new Error('Configuration error. Please refresh and try again.');
      }

      const response = await fetch(`${baseUrl}/functions/v1/connect-start`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start Stripe Connect setup');
      }

      const { url } = await response.json();

      if (!url) {
        throw new Error('No onboarding URL received');
      }

      // Redirect to Stripe Connect onboarding
      window.location.href = url;
    } catch (err) {
      console.error('Error restarting Stripe Connect setup:', err);
      setError(err instanceof Error ? err.message : 'Failed to restart setup');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FEF6D6] p-6">
      <div className="max-w-md w-full bg-white rounded-2xl border border-[#2C2731] p-8 shadow-sm">
        <div className="flex flex-col items-center text-center">
          <div className="h-16 w-16 rounded-full bg-orange-100 flex items-center justify-center mb-4">
            <ArrowPathIcon className="h-8 w-8 text-orange-600" />
          </div>

          <h1 className="text-2xl font-semibold text-[#2C2731] mb-2">
            Setup Link Expired
          </h1>

          <p className="text-[#2C2731]/70 mb-6">
            Your Stripe Connect setup link has expired. This happens when you navigate away or close the
            window during setup. Don't worry - you can easily generate a new link to continue.
          </p>

          {error && (
            <div className="w-full mb-6 p-4 rounded-lg border border-red-300 bg-red-100">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="w-full bg-[#E8DFBD] rounded-lg p-4 mb-6 text-left">
            <h3 className="text-sm font-medium text-[#2C2731] mb-2">What happens next?</h3>
            <ul className="space-y-2 text-sm text-[#2C2731]/70">
              <li className="flex items-start gap-2">
                <span className="text-[#2C2731] font-medium">1.</span>
                <span>Click "Generate New Link" to create a fresh setup link</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#2C2731] font-medium">2.</span>
                <span>You'll be redirected to Stripe to continue where you left off</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#2C2731] font-medium">3.</span>
                <span>Complete any remaining verification steps</span>
              </li>
            </ul>
          </div>

          <div className="w-full space-y-3">
            <button
              onClick={handleRetry}
              disabled={loading}
              className={`w-full px-5 py-2.5 rounded-md text-[#FEF6D6] transition-colors text-sm font-medium ${
                loading
                  ? 'bg-[#2C2731]/40 cursor-not-allowed'
                  : 'bg-[#2C2731] hover:bg-[#241F2A]'
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Generating Link...
                </span>
              ) : (
                'Generate New Link'
              )}
            </button>

            <button
              onClick={handleCancel}
              disabled={loading}
              className="w-full px-5 py-2.5 rounded-md border border-[#2C2731] text-[#2C2731] hover:bg-[#E8DFBD] transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel & Return to Dashboard
            </button>
          </div>

          <p className="mt-6 text-xs text-[#2C2731]/60">
            Need help? Contact support if you're experiencing issues with your setup.
          </p>
        </div>
      </div>
    </div>
  );
}
